/**
 * Rich Filters - Vegiwise Theme
 * JavaScript for enhanced filter components: combobuttons, search input, toggle switch
 * 
 * Requirements:
 * - 2.2: Search input with dropdown for Culture filter
 * - 2.3: Toggle switch for availability filter
 * - 4.1: Auto-apply on desktop (configurable)
 * - 4.2: Manual apply on mobile
 * - 4.3: Preserve sort_by parameter
 */

(function() {
  'use strict';

  // Mobile breakpoint (matches Shopify Dawn theme)
  const MOBILE_BREAKPOINT = 990;

  /**
   * Check if current viewport is mobile
   * @returns {boolean}
   */
  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  /**
   * Check if auto-apply is enabled from the facets container
   * @returns {boolean}
   */
  function isAutoApplyEnabled() {
    const facetsContainer = document.querySelector('.facets-container');
    if (facetsContainer) {
      return facetsContainer.dataset.autoApply === 'true';
    }
    return false;
  }

  /**
   * Get current sort_by parameter from URL
   * @returns {string|null}
   */
  function getCurrentSortBy() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('sort_by');
  }

  /**
   * Build URL with filter parameters, preserving sort_by
   * @param {HTMLFormElement} form - The form element
   * @returns {string} - URL search params string
   */
  function buildFilterUrl(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    
    // Preserve sort_by if it exists in current URL but not in form
    const currentSortBy = getCurrentSortBy();
    if (currentSortBy && !params.has('sort_by')) {
      params.set('sort_by', currentSortBy);
    }
    
    return params.toString();
  }

  /**
   * Trigger filter application via the native facet system
   * Only triggers if auto-apply is enabled and not on mobile
   * @param {Event} event - The triggering event
   */
  function triggerFilterApply(event) {
    // Check if auto-apply is enabled
    if (!isAutoApplyEnabled()) {
      return;
    }
    
    // Don't auto-apply on mobile
    if (isMobile()) {
      return;
    }
    
    // Find the closest facet-filters-form element
    const facetForm = event.target.closest('facet-filters-form');
    if (facetForm) {
      const form = facetForm.querySelector('form');
      if (form) {
        // Dispatch input event to trigger the native debounced submit
        form.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  /**
   * Prevent native auto-apply if disabled
   * This intercepts the native facets.js behavior
   */
  function setupAutoApplyControl() {
    // If auto-apply is disabled, we need to prevent the native behavior
    if (!isAutoApplyEnabled()) {
      // Override the native facet form behavior
      document.querySelectorAll('facet-filters-form').forEach(function(facetForm) {
        const form = facetForm.querySelector('form');
        if (form) {
          // Remove the native input listener by cloning the form
          // This is a workaround since we can't directly remove the listener
          form.addEventListener('input', function(e) {
            // Only prevent if it's from our rich filter inputs
            if (e.target.closest('.vw-rich-filter')) {
              e.stopPropagation();
            }
          }, true);
        }
      });
    }
  }


  // ==========================================================================
  // Search Filter Component (Culture Filter)
  // Requirement 2.2: Search input with dropdown
  // ==========================================================================

  /**
   * Initialize search filter functionality
   * Handles dynamic filtering of dropdown options based on search input
   */
  function initSearchFilters() {
    const searchFilters = document.querySelectorAll('.vw-search-filter');
    
    searchFilters.forEach(function(container) {
      const input = container.querySelector('[data-search-input]');
      const dropdown = container.querySelector('[data-dropdown]');
      const chipsContainer = container.querySelector('[data-selected-chips]');
      
      if (!input || !dropdown) return;
      
      // Filter dropdown options on input
      input.addEventListener('input', function(e) {
        const searchTerm = e.target.value.toLowerCase().trim();
        const options = dropdown.querySelectorAll('.vw-search-filter__option');
        
        options.forEach(function(option) {
          const label = option.getAttribute('data-label') || '';
          const matches = label.includes(searchTerm);
          option.classList.toggle('hidden', !matches && searchTerm !== '');
        });
        
        // Update aria-expanded
        input.setAttribute('aria-expanded', 'true');
      });
      
      // Handle checkbox changes in dropdown
      dropdown.addEventListener('change', function(e) {
        if (e.target.type === 'checkbox') {
          const option = e.target.closest('.vw-search-filter__option');
          if (option) {
            option.classList.toggle('active', e.target.checked);
          }
          
          // Update chips display
          updateSearchChips(container);
          
          // Try to trigger auto-apply (will check if enabled)
          triggerFilterApply(e);
        }
      });
      
      // Handle chip removal
      if (chipsContainer) {
        chipsContainer.addEventListener('click', function(e) {
          const removeBtn = e.target.closest('.vw-chip__remove');
          if (removeBtn) {
            e.preventDefault();
            const value = removeBtn.dataset.removeValue;
            const param = removeBtn.dataset.param;
            
            // Find and uncheck the corresponding checkbox
            const checkbox = dropdown.querySelector(
              `input[name="${param}"][value="${value}"]`
            );
            if (checkbox) {
              checkbox.checked = false;
              const option = checkbox.closest('.vw-search-filter__option');
              if (option) {
                option.classList.remove('active');
              }
            }
            
            // Update chips display
            updateSearchChips(container);
            
            // Try to trigger auto-apply
            triggerFilterApply(e);
          }
        });
      }
      
      // Clear search on focus out (optional UX improvement)
      input.addEventListener('blur', function() {
        // Delay to allow click on dropdown options
        setTimeout(function() {
          // Don't clear if dropdown has focus
          if (!dropdown.contains(document.activeElement)) {
            // Show all options
            const options = dropdown.querySelectorAll('.vw-search-filter__option');
            options.forEach(function(option) {
              option.classList.remove('hidden');
            });
          }
        }, 200);
      });
    });
  }

  /**
   * Update the chips display for a search filter
   * @param {HTMLElement} container - The search filter container
   */
  function updateSearchChips(container) {
    const chipsContainer = container.querySelector('[data-selected-chips]');
    const dropdown = container.querySelector('[data-dropdown]');
    
    if (!chipsContainer || !dropdown) return;
    
    const checkedInputs = dropdown.querySelectorAll('input[type="checkbox"]:checked');
    
    // Clear existing chips
    chipsContainer.innerHTML = '';
    
    // Create chips for each checked value
    checkedInputs.forEach(function(input) {
      const option = input.closest('.vw-search-filter__option');
      const label = option ? option.querySelector('.vw-search-filter__option-label') : null;
      const labelText = label ? label.textContent.trim() : input.value;
      
      const chip = document.createElement('div');
      chip.className = 'vw-chip';
      chip.dataset.value = input.value;
      chip.innerHTML = `
        <span class="vw-chip__label">${escapeHtml(labelText)}</span>
        <button type="button" 
                class="vw-chip__remove" 
                aria-label="Supprimer le filtre"
                data-remove-value="${escapeHtml(input.value)}"
                data-param="${escapeHtml(input.name)}">
          <svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 12 12">
            <path d="M10.5 1.5L1.5 10.5M1.5 1.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      `;
      chipsContainer.appendChild(chip);
    });
    
    // Toggle visibility of chips container
    chipsContainer.classList.toggle('hidden', checkedInputs.length === 0);
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} - Escaped string
   */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }


  // ==========================================================================
  // Toggle Switch Component (Availability Filter)
  // Requirement 2.3: Toggle switch for availability
  // ==========================================================================

  /**
   * Initialize toggle switch functionality
   * Handles state changes and syncs with hidden checkbox
   */
  function initToggleSwitches() {
    const toggleSwitches = document.querySelectorAll('.vw-toggle-switch');
    
    toggleSwitches.forEach(function(toggle) {
      toggle.addEventListener('click', function(e) {
        e.preventDefault();
        
        const isChecked = toggle.getAttribute('aria-checked') === 'true';
        const newState = !isChecked;
        
        // Update toggle state
        toggle.setAttribute('aria-checked', newState.toString());
        
        // Update the row active state
        const row = toggle.closest('.vw-toggle-filter__row');
        if (row) {
          row.classList.toggle('active', newState);
        }
        
        // Sync with hidden checkbox
        const container = toggle.closest('.vw-toggle-filter');
        if (container) {
          const hiddenCheckbox = container.querySelector('.vw-toggle-filter__input');
          if (hiddenCheckbox) {
            hiddenCheckbox.checked = newState;
          }
        }
        
        // Try to trigger auto-apply
        triggerFilterApply(e);
      });
      
      // Keyboard support
      toggle.addEventListener('keydown', function(e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          toggle.click();
        }
      });
    });
  }

  // ==========================================================================
  // Combobuttons Component (Month Filters)
  // ==========================================================================

  /**
   * Initialize combobutton functionality
   * Adds auto-apply behavior for desktop (if enabled)
   */
  function initCombobuttons() {
    const combobuttons = document.querySelectorAll('.vw-combobuttons');
    
    combobuttons.forEach(function(container) {
      container.addEventListener('change', function(e) {
        if (e.target.type === 'checkbox') {
          const label = e.target.closest('.vw-combobutton');
          if (label) {
            label.classList.toggle('active', e.target.checked);
          }
          
          // Try to trigger auto-apply
          triggerFilterApply(e);
        }
      });
    });
  }

  // ==========================================================================
  // Checkbox Filter Component (Fallback)
  // ==========================================================================

  /**
   * Initialize checkbox filter functionality
   * Adds auto-apply behavior for desktop (if enabled)
   */
  function initCheckboxFilters() {
    const checkboxFilters = document.querySelectorAll('.vw-checkbox-filter');
    
    checkboxFilters.forEach(function(container) {
      container.addEventListener('change', function(e) {
        if (e.target.type === 'checkbox') {
          const label = e.target.closest('.vw-checkbox-filter__label');
          if (label) {
            label.classList.toggle('active', e.target.checked);
          }
          
          // Try to trigger auto-apply
          triggerFilterApply(e);
        }
      });
    });
  }


  /**
   * Initialize Apply button functionality
   * Submits the form when Apply button is clicked
   */
  function initApplyButtons() {
    // Find all Apply buttons in mobile facets
    const applyButtons = document.querySelectorAll('.mobile-facets__footer .button--primary');
    
    applyButtons.forEach(function(button) {
      // Remove the default onclick handler and add our own
      button.removeAttribute('onclick');
      
      button.addEventListener('click', function(e) {
        e.preventDefault();
        
        // Find the form
        const form = button.closest('form');
        if (form) {
          // Submit the form by dispatching an input event (triggers native facet behavior)
          form.dispatchEvent(new Event('input', { bubbles: true }));
          
          // Close the drawer after a short delay
          setTimeout(function() {
            const wrapper = button.closest('.mobile-facets__wrapper');
            if (wrapper) {
              const summary = wrapper.querySelector('summary');
              if (summary) {
                summary.click();
              }
            }
          }, 100);
        }
      });
    });
  }


  // ==========================================================================
  // Initialization
  // ==========================================================================

  /**
   * Initialize all rich filter components
   */
  function initRichFilters() {
    setupAutoApplyControl();
    initSearchFilters();
    initToggleSwitches();
    initCombobuttons();
    initCheckboxFilters();
    initApplyButtons();
  }

  /**
   * Re-initialize after AJAX content updates
   * Called when facets are re-rendered by the native system
   */
  function reinitRichFilters() {
    // Small delay to ensure DOM is updated
    requestAnimationFrame(function() {
      initRichFilters();
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRichFilters);
  } else {
    initRichFilters();
  }

  // Re-initialize after Shopify section rendering
  document.addEventListener('shopify:section:load', reinitRichFilters);
  
  // Also observe for dynamic content changes (facet updates)
  const observer = new MutationObserver(function(mutations) {
    let shouldReinit = false;
    
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if rich filter elements were added
            if (node.classList && (
              node.classList.contains('vw-rich-filter') ||
              node.querySelector && node.querySelector('.vw-rich-filter')
            )) {
              shouldReinit = true;
            }
          }
        });
      }
    });
    
    if (shouldReinit) {
      reinitRichFilters();
    }
  });

  // Observe the facets container for changes
  const facetsContainer = document.querySelector('.facets-container');
  if (facetsContainer) {
    observer.observe(facetsContainer, {
      childList: true,
      subtree: true
    });
  }

  // Also observe mobile facets
  const mobileFacets = document.querySelector('.mobile-facets__wrapper');
  if (mobileFacets) {
    observer.observe(mobileFacets, {
      childList: true,
      subtree: true
    });
  }

  // Expose for external use if needed
  window.VegiwiseRichFilters = {
    init: initRichFilters,
    reinit: reinitRichFilters,
    isMobile: isMobile,
    isAutoApplyEnabled: isAutoApplyEnabled
  };

})();
